import React, { useCallback } from 'react';
import { isFalsy } from '@core/helpers/boolean';
import { useContextTranslation } from '@core/i18n';
import { isLiveStream, isLiveStreamIdle, isLiveStreamReplay, } from '@core/livestream/helpers';
import { ShareEnum } from '@core/types';
import UnstyledButton from '@embed/components/common/UnstyledButton';
import { ModalType } from '@embed/components/VideoPlayer/Player3/components/modals/VideoSettingsMenu/types';
import { PlayerModal } from '@embed/components/VideoPlayer/Player3/components/PlayerModal';
import { useShareMessage } from '@embed/components/VideoPlayer/Player3/hooks/useCommonTranslations';
import { EventType, useCustomPlayerVideoEvent, } from '@embed/components/VideoPlayer/Player3/hooks/useCustomPlayerVideoEvent';
import { useStore } from '@embed/components/VideoPlayer/Player3/store';
import { usePromoUrl } from '@embed/hooks/usePromoUrl/usePromoUrl';
import { useVideoType } from '@embed/hooks/useVideoType';
import Modal from '@player/components/Modal';
import { PlayerModalType, selectors, } from '@player/features/modal/modalRegistry';
import { EMBED_IN_EMAIL_BUTTON, SHARE_BUTTON, TOGGLE_QR_CODE_BUTTON, } from '@player/testIds';
import { IVSQualityButton } from '../IVSQualityButton/IVSQualityButton';
import { FlexColumn, MainMenuContainer, VideoCaption } from './MainMenu.styles';
/**
 * Main menu modal.
 */
export const MainMenu = ({ onClose, setReturnFocus }) => {
    const { t } = useContextTranslation();
    const { showModal, hideModal } = useStore(selectors.modalActionsSelector);
    const isQRModeEnabled = useStore((state) => state.isQRModeEnabled);
    const video = useStore((state) => state.video);
    const setQRMode = useStore((state) => state.actions.setQRMode);
    const params = useStore((state) => state.params);
    const videoType = useVideoType(video);
    const customPlayerVideoEvent = useCustomPlayerVideoEvent();
    const shareMessage = useShareMessage(video);
    const handleShare = useCallback(() => {
        onClose();
        showModal(ModalType.SHARE, PlayerModalType.SHARE, {
            shareMessage,
            onDismiss: () => hideModal(ModalType.SHARE),
            onLinkCopy: () => {
                showModal(ModalType.COPY, PlayerModalType.SHARE_COPY_MESSAGE, {
                    setReturnFocus,
                });
                setTimeout(() => {
                    hideModal(ModalType.COPY);
                    hideModal(ModalType.SHARE);
                }, 2000);
            },
        });
    }, [hideModal, onClose, showModal, shareMessage, setReturnFocus]);
    const handleEmailEmbed = useCallback(() => {
        onClose();
        showModal(ModalType.EMAIL_EMBED, PlayerModalType.EMAIL_EMBED, {
            videoId: video?.id ?? '',
            onClose: () => {
                hideModal(ModalType.EMAIL_EMBED);
            },
        });
        customPlayerVideoEvent({ type: EventType.FW_VIDEO_CLICK_EMAIL_EMBED });
    }, [onClose, showModal, video?.id, customPlayerVideoEvent, hideModal]);
    const handleToggleQRCode = useCallback(() => {
        setQRMode?.(!isQRModeEnabled);
        customPlayerVideoEvent({ type: EventType.FW_VIDEO_CLICK_QR_CODE });
        onClose();
    }, [setQRMode, isQRModeEnabled, onClose, customPlayerVideoEvent]);
    const isEmailEmbedFeatureEnabled = !isFalsy(params?.email_embed) &&
        (!isLiveStream(video) ||
            isLiveStreamReplay(video) ||
            isLiveStreamIdle(video));
    const isToggleQRFeatureEnabled = !isFalsy(params?.qr_code);
    const promoUrl = usePromoUrl({ product: videoType, widget: 'player' });
    return (<PlayerModal onBackdropClick={onClose}>
      <Modal.Menu promoUrl={promoUrl}>
        <FlexColumn>
          <VideoCaption style={{
            color: 'var(--white)',
        }}>
            {video?.caption}
          </VideoCaption>
          <MainMenuContainer className="cy-more-menu">
            <Modal.Group>
              {params?.share !== ShareEnum.FALSE && (<Modal.Item button>
                  <UnstyledButton className="cy-share" onClick={handleShare} data-testid={SHARE_BUTTON}>
                    {t('Share')}
                  </UnstyledButton>
                </Modal.Item>)}
              {isEmailEmbedFeatureEnabled && (<Modal.Item button>
                  <UnstyledButton onClick={handleEmailEmbed} data-testid={EMBED_IN_EMAIL_BUTTON}>
                    {t('player:Embed to Email')}
                  </UnstyledButton>
                </Modal.Item>)}
              {isToggleQRFeatureEnabled && (<Modal.Item button>
                  <UnstyledButton onClick={handleToggleQRCode} data-testid={TOGGLE_QR_CODE_BUTTON}>
                    {t('player:Toggle QR Code')}
                  </UnstyledButton>
                </Modal.Item>)}
            </Modal.Group>
            <Modal.Group>
              <IVSQualityButton onClose={onClose}/>
            </Modal.Group>
            <Modal.Group>
              <Modal.Item button>
                <UnstyledButton aria-label={t('player:Exit menu')} className="cy-cancel" onClick={onClose}>
                  {t('player:Cancel')}
                </UnstyledButton>
              </Modal.Item>
            </Modal.Group>
          </MainMenuContainer>
        </FlexColumn>
      </Modal.Menu>
    </PlayerModal>);
};
